import {
    formatDateLocalISO,
    startOfDay,
} from "crqlar-react-common/dist/utils/date.util";
import type {DatePickerChangeReason} from "../../common/newDatePicker/datePickerShared";
import {readStoredDateSelection, type SectorDateSelection} from "../sectorDate";

export const FLOORPLAN_QUERY_PARAMS = {
    restaurantId: "restaurantId",
    date: "date",
    dayPart: "dayPart",
    timeStart: "timeStart",
    timeEnd: "timeEnd",
    statuses: "statuses",
} as const;

export const floorplanSearchParamsForRestaurant = (
    currentSearchParams: string | URLSearchParams,
    restaurantId: string,
): URLSearchParams => {
    const nextSearchParams = new URLSearchParams(currentSearchParams);
    nextSearchParams.set(FLOORPLAN_QUERY_PARAMS.restaurantId, restaurantId);
    return nextSearchParams;
};

export const FLOORPLAN_DAY_SLICES = ["now", "morning", "day", "evening", "custom"] as const;
export type FloorplanDaySlice = typeof FLOORPLAN_DAY_SLICES[number];

/** The floor plan day is the sector day shared with the calendar — see `sectorDate.ts`. */
export type FloorplanDateSelection = SectorDateSelection;

export const isValidDate = (date: Date | undefined | null): date is Date => (
    Boolean(date) && !Number.isNaN(date?.getTime())
);

export const isFloorplanDaySlice = (value: string | null | undefined): value is FloorplanDaySlice => (
    Boolean(value) && FLOORPLAN_DAY_SLICES.includes(value as FloorplanDaySlice)
);

export const isFloorplanTime = (value: string | null | undefined): value is string => (
    Boolean(value) && /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value as string)
);

/**
 * Per-restaurant floor plan preferences. The selected day is deliberately absent:
 * it lives in the sector-wide key so the calendar stays on the same date.
 */
export const floorplanStorageKeys = (restaurantId: string) => ({
    dayPart: `floorplanDaySlice-${restaurantId}`,
    timeStart: `floorplanStartTime-${restaurantId}`,
    timeEnd: `floorplanEndTime-${restaurantId}`,
    statuses: `selectedStatuses-${restaurantId}-floorplan`,
});

export const parseFloorplanDateParam = (value: string | null): FloorplanDateSelection | undefined => {
    if (value === "today") {
        return {date: startOfDay(new Date()), mode: "today"};
    }
    if (!value) return undefined;

    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) return undefined;

    const [, yearValue, monthValue, dayValue] = match;
    const year = Number(yearValue);
    const month = Number(monthValue);
    const day = Number(dayValue);
    const date = new Date(year, month - 1, day);

    if (
        date.getFullYear() !== year
        || date.getMonth() !== month - 1
        || date.getDate() !== day
    ) {
        return undefined;
    }

    return {date: startOfDay(date), mode: "fixed"};
};

export const readFloorplanStoredDate = readStoredDateSelection;

export const floorplanDateParam = ({date, mode}: FloorplanDateSelection): string => (
    mode === "today" ? "today" : formatDateLocalISO(date)!
);

export const floorplanDateSelection = (
    date: Date,
    reason: DatePickerChangeReason = "selection",
): FloorplanDateSelection => ({
    date: startOfDay(date),
    mode: reason === "today" ? "today" : "fixed",
});

export const parseFloorplanStatuses = <T extends string>(
    value: string | null,
    available: readonly T[],
): T[] | undefined => {
    if (value === null) return undefined;
    if (value === "") return [];

    const statuses = value.split(",");
    return statuses.every(status => available.includes(status as T))
        ? statuses as T[]
        : undefined;
};

export const serializeFloorplanStatuses = (statuses: readonly string[]): string => statuses.join(",");
